import { keepPreviousData, QueryClient, skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import userService from '../services/user.service';
import organizationService, { OrgApiParams } from '../services/organization.service';
import { invalidateOrgCaches } from './useOrgQueries';
import { UpdateManagedUserData, InviteUserData } from '../types/auth.types';

const STALE_MS = 30_000;

export interface UserListParams {
  search: string;
  company: string | 'ALL' | null;
  team: string | null;
  department: string | null;
  activeOnly: boolean;
  page: number;
}

export function userApiParams(p: UserListParams): OrgApiParams {
  const params: OrgApiParams = { page: p.page };
  if (p.search) params.search = p.search;
  if (p.company && p.company !== 'ALL') params.company = p.company;
  if (p.team) params.team = p.team;
  if (p.department) params.department = p.department;
  if (p.activeOnly) params.is_active = true;
  return params;
}

// Page hook

export function useUsersPage(p: UserListParams) {
  return useQuery({
    queryKey: ['users-page', p],
    queryFn: () => userService.getUsersPage(userApiParams(p)),
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });
}

// Detail hooks

export function useUserDetail(userId: string | null) {
  return useQuery({
    queryKey: ['user-detail', userId],
    queryFn: userId == null ? skipToken : () => userService.getUser(userId),
    staleTime: STALE_MS,
  });
}

export function useUserRoles(userId: string | null) {
  return useQuery({
    queryKey: ['user-roles', userId],
    queryFn: userId == null ? skipToken : () => organizationService.getUserRolesFor(userId),
    staleTime: STALE_MS,
  });
}

// Tree hook — backs the role dialog's cascading org selects with the whole
// accessible hierarchy in one request. The endpoint walks that whole
// hierarchy server-side, so callers gate the fetch behind `enabled` (e.g.
// only while the dialog is open).

export function useOrgTreeData(enabled = true) {
  return useQuery({
    queryKey: ['org-tree'],
    queryFn: () => organizationService.getOrganizationTreeData(),
    staleTime: STALE_MS,
    enabled,
  });
}

// Mutations
//
// User rows denormalize their role summaries and the tree denormalizes the
// role edges, so any user or role write can stale every user cache family.
// User-only writes (update, invite) refresh just these four; writes that
// touch roles — user delete cascades them, role mutations by definition —
// call invalidateOrgCaches instead, which covers these four families plus
// the org ones whose user counts they change. Never call both: the double
// invalidation cancels and restarts in-flight refetches, duplicating HTTP
// requests.
function invalidateUserCaches(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['users-page'] });
  queryClient.invalidateQueries({ queryKey: ['user-detail'] });
  queryClient.invalidateQueries({ queryKey: ['user-roles'] });
  queryClient.invalidateQueries({ queryKey: ['org-tree'] });
}

// UserRole rows bind to exactly one org level; the backend rejects anything
// else with a serializer error — on partial updates too. This guard turns
// that contract into an immediate programmer-facing throw instead of a
// runtime 400. Empty strings count as unset so form empty-select sentinels
// neither trip the guard nor pass as a binding.
export function assertExactlyOneOrg(payload: {
  company?: string | null;
  team?: string | null;
  department?: string | null;
}): void {
  const setLevels = [payload.company, payload.team, payload.department].filter(
    (level) => level != null && level !== '',
  );
  if (setLevels.length !== 1) {
    throw new Error('Exactly one of company, team, or department must be set on a user role.');
  }
}

// User mutations

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateManagedUserData }) =>
      userService.updateUser(id, data),
    onSuccess: () => invalidateUserCaches(queryClient),
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: InviteUserData) => userService.inviteUser(data),
    onSuccess: () => invalidateUserCaches(queryClient),
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: (_result, id) => {
      // Drop the deleted user's caches outright — browser-back to its URL
      // must not re-render the detail or its roles list from cache within
      // staleTime.
      queryClient.removeQueries({ queryKey: ['user-detail', id] });
      queryClient.removeQueries({ queryKey: ['user-roles', id] });
      // The user's roles cascade with the delete, changing org user counts;
      // the org fan-out spans the user cache families too.
      invalidateOrgCaches(queryClient);
    },
  });
}

// Role mutations
//
// One invalidateOrgCaches call per write — it spans the user cache families
// as well, and role writes change the user counts the org entities
// denormalize.

// The serializer re-validates the exactly-one binding on every write, PATCH
// included: a role-only payload 400s. Updates must echo the role's existing
// binding at the same level; moving a role across levels is delete +
// recreate.
export type UserRoleOrgBinding =
  | { company: string; team?: never; department?: never }
  | { company?: never; team: string; department?: never }
  | { company?: never; team?: never; department: string };

export interface CreateUserRolePayload {
  email: string;
  role: 'ADMIN' | 'MANAGER' | 'USER';
  company?: string;
  team?: string;
  department?: string;
}

export type UpdateUserRolePayload = {
  role?: 'ADMIN' | 'MANAGER' | 'USER';
} & UserRoleOrgBinding;

export function useCreateUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateUserRolePayload) => {
      assertExactlyOneOrg(data);
      return organizationService.createUserRole(data);
    },
    onSuccess: () => invalidateOrgCaches(queryClient),
  });
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRolePayload }) => {
      assertExactlyOneOrg(data);
      return organizationService.updateUserRole(id, data);
    },
    onSuccess: () => invalidateOrgCaches(queryClient),
  });
}

export function useDeleteUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => organizationService.deleteUserRole(id),
    onSuccess: () => invalidateOrgCaches(queryClient),
  });
}
